class SpaFinder extends HTMLElement {
  constructor() {
    super();

    this.searchInput = this.querySelector('.js-search-input');
    this.noResultsMessage = this.querySelector('.js-no-results-message');
    this.locations = [...this.querySelectorAll('.js-location-item')];
    this.locationsList = this.querySelector('.js-locations-list');
    this.markers = [...this.querySelectorAll('gmp-advanced-marker')];
    this.filterNavigation = this.querySelector('.js-filter-navigation');
    this.filtersScroller = this.querySelector('.js-location-filters');
    this.filterButtons = [...this.querySelectorAll('.js-location-filter')];
    this.previousFilterButton = this.querySelector('.js-filter-arrow-previous');
    this.nextFilterButton = this.querySelector('.js-filter-arrow-next');
    this.activeType = this.querySelector('.js-location-filter[aria-pressed="true"]')?.dataset.type || '';
    this.handleSearchBound = this.handleSearch.bind(this);
    this.handleLocationListClickBound = this.handleLocationListClick.bind(this);
    this.handleMarkerClickBound = this.handleMarkerClick.bind(this);
    this.handleFilterClickBound = this.handleFilterClick.bind(this);
    this.handleFilterArrowClickBound = this.handleFilterArrowClick.bind(this);
    this.updateFilterNavigationBound = this.updateFilterNavigation.bind(this);
  }

  connectedCallback() {
    if (this.searchInput) this.searchInput.addEventListener('input', this.handleSearchBound);
    this.locationsList.addEventListener('click', this.handleLocationListClickBound);
    this.markers.forEach(marker => marker.addEventListener('click', this.handleMarkerClickBound));
    this.filtersScroller?.addEventListener('click', this.handleFilterClickBound);
    this.previousFilterButton?.addEventListener('click', this.handleFilterArrowClickBound);
    this.nextFilterButton?.addEventListener('click', this.handleFilterArrowClickBound);
    this.filtersScroller?.addEventListener('scroll', this.updateFilterNavigationBound, { passive: true });
    window.addEventListener('resize', this.updateFilterNavigationBound);
    requestAnimationFrame(this.updateFilterNavigationBound);
  }

  disconnectedCallback() {
    if (this.searchInput) this.searchInput?.removeEventListener('input', this.handleSearchBound);
    this.locationsList.removeEventListener('click', this.handleLocationListClickBound);
    this.markers.forEach(marker => marker.removeEventListener('click', this.handleMarkerClickBound));
    this.filtersScroller?.removeEventListener('click', this.handleFilterClickBound);
    this.previousFilterButton?.removeEventListener('click', this.handleFilterArrowClickBound);
    this.nextFilterButton?.removeEventListener('click', this.handleFilterArrowClickBound);
    this.filtersScroller?.removeEventListener('scroll', this.updateFilterNavigationBound);
    window.removeEventListener('resize', this.updateFilterNavigationBound);
  }

  handleFilterClick(event) {
    const filterButton = event.target.closest('.js-location-filter');
    if (!filterButton || !this.filtersScroller?.contains(filterButton)) return;

    this.activeType = filterButton.dataset.type || '';
    this.filterButtons.forEach((button) => {
      const isActive = button === filterButton;

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    const query = this.searchInput?.value.trim().toLowerCase() || '';
    this.filterLocations(query);
  }

  handleFilterArrowClick(event) {
    if (!this.filtersScroller) return;

    const direction = event.currentTarget === this.previousFilterButton ? -1 : 1;
    const scrollDistance = Math.max(this.filtersScroller.clientWidth * 0.8, 120);

    this.filtersScroller.scrollBy({
      left: direction * scrollDistance,
      behavior: 'smooth',
    });
  }

  updateFilterNavigation() {
    if (!this.filterNavigation || !this.filtersScroller) return;

    const maxScrollLeft = this.filtersScroller.scrollWidth - this.filtersScroller.clientWidth;
    const hasOverflow = maxScrollLeft > 1;
    const isAtStart = this.filtersScroller.scrollLeft <= 1;
    const isAtEnd = this.filtersScroller.scrollLeft >= maxScrollLeft - 1;

    this.filterNavigation.classList.toggle('has-overflow', hasOverflow);
    if (this.previousFilterButton) this.previousFilterButton.disabled = !hasOverflow || isAtStart;
    if (this.nextFilterButton) this.nextFilterButton.disabled = !hasOverflow || isAtEnd;
  }

  handleSearch(e) {
    const query = e.target.value.trim().toLowerCase();
    this.filterLocations(query);
  }

  filterLocations(query) {
    let visibleCount = 0;
    let firstVisibleLocation = null;

    this.locations.forEach((location, index) => {
      const title = location.dataset.title?.toLowerCase() || '';
      const address = location.dataset.address?.toLowerCase() || '';
      const locationType = location.dataset.type || '';
      const marker = this.markers[index];

      const matchesSearch = title.includes(query) || address.includes(query);
      const matchesType = this.activeType === '' || locationType === this.activeType;
      const matches = matchesSearch && matchesType;

      location.style.display = matches ? '' : 'none';
      marker?.classList.toggle('is-hidden', !matches);
      if (!matches) marker?.classList.remove('is-active');

      if (matches) {
        visibleCount += 1;
        if (!firstVisibleLocation) firstVisibleLocation = location;
      }
    });

    const noResults = (query !== '' || this.activeType !== '') && visibleCount === 0;
    this.toggleNoResults(noResults);

    if (!firstVisibleLocation) {
      this.resetActiveLocationStyles();
      this.resetActiveMarkerStyles();
      return;
    }

    const locationId = firstVisibleLocation.dataset.locationId;
    if (locationId) this.setActiveLocation(locationId);
  }

  toggleNoResults(noResults) {
    this.noResultsMessage?.toggleAttribute('hidden', !noResults);
    this.locationsList?.toggleAttribute('hidden', noResults);
  }

  handleLocationListClick(e) {
    if (e.target.closest('a')) return;

    const locationEl = e.target.closest('.js-location-item');
    if (!locationEl || !this.locationsList?.contains(locationEl)) return;

    const locationId = locationEl.dataset.locationId;
    if (locationId) this.setActiveLocation(locationId);
  }

  handleMarkerClick(e) {
    const markerEl = e.target.closest('gmp-advanced-marker');
    if (!markerEl || markerEl.classList.contains('is-hidden')) return;

    const locationId = markerEl.dataset.locationId;
    if (locationId) this.setActiveLocation(locationId);
  }

  setActiveLocation(locationId) {
    if (!locationId) return;

    const locationEl = this.querySelector(`.js-location-item[data-location-id="${locationId}"]`);
    if (!locationEl) return;

    const latitude = Number(locationEl.dataset.latitude);
    const longitude = Number(locationEl.dataset.longitude);

    this.setMapCenter({ latitude, longitude });
    this.zoomMap();
    this.resetActiveMarkerStyles();
    this.setActiveMarkerStyles(locationId);
    this.resetActiveLocationStyles();
    this.setActiveLocationStyles(locationId);
    this.scrollToLocation(locationId);
  }

  setActiveLocationStyles(locationId) {
    const locationEl = this.querySelector(`.js-location-item[data-location-id="${locationId}"]`);
    if (locationEl) locationEl.classList.add('is-active');
  }

  scrollToLocation(locationId) {
    const locationsListEl = this.locationsList;
    const locationEl = this.querySelector(`.js-location-item[data-location-id="${locationId}"]`);
    if (!locationEl || !locationsListEl) return;

    const listRect = locationsListEl.getBoundingClientRect();
    const locationRect = locationEl.getBoundingClientRect();
    const nextScrollTop = locationRect.top - listRect.top + locationsListEl.scrollTop;

    locationsListEl.scrollTo({
      top: nextScrollTop,
      behavior: 'smooth',
    });
  }

  resetActiveLocationStyles() {
    const activeLocationEl = this.querySelector('.js-location-item.is-active');
    if (activeLocationEl) activeLocationEl.classList.remove('is-active');
  }

  setMapCenter({ latitude, longitude }) {
    const map = this.querySelector('.js-map')?.innerMap;
    if (map && map.panTo) map.panTo({ lat: latitude, lng: longitude });
  }

  zoomMap() {
    const map = this.querySelector('.js-map')?.innerMap;
    const currentMapZoom = map?.getZoom();

    if (map && currentMapZoom < 14) map.setZoom(14);
  }

  resetActiveMarkerStyles() {
    const prevActiveMarketEl = this.querySelector("gmp-advanced-marker.is-active");
    if (prevActiveMarketEl) prevActiveMarketEl.classList.remove('is-active');
  }

  setActiveMarkerStyles(locationId) {
    const newActiveMarkerEl = this.querySelector(`gmp-advanced-marker[data-location-id="${locationId}"]`);
    if (newActiveMarkerEl) newActiveMarkerEl.classList.add('is-active');
  }
}

customElements.define('spa-finder', SpaFinder);